// ============================================================================
// PLATFORM COMMAND CENTER — Platform Admin only
//
// Four verdicts: Tenant Health, Tenant Risk, Configuration Integrity, Platform Value
// Signals → Metrics → Verdicts → Actions
// Zero PHI reads.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Building, Users, Shield, Activity, AlertTriangle, CheckCircle,
  XCircle, TrendingUp, TrendingDown, Clock, Settings, ChevronDown,
  ChevronUp, RefreshCw, Minus, BarChart3, Zap, ArrowRight,
} from 'lucide-react';
import {
  loadPlatformHealth,
  RISK, RISK_LABELS, RISK_SEVERITY,
  HEALTH_VERDICT, RISK_VERDICT, CONFIG_VERDICT, VALUE_VERDICT,
} from '../core/platformHealth.js';

// ============================================================================
// VERDICT COLORS + ICONS
// ============================================================================

const VERDICT_STYLE = {
  [HEALTH_VERDICT.HEALTHY]:  { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle },
  [HEALTH_VERDICT.WARNING]:  { color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   icon: AlertTriangle },
  [HEALTH_VERDICT.CRITICAL]: { color: 'text-red-400',     bg: 'bg-red-500/10',      border: 'border-red-500/20',     icon: XCircle },
  [HEALTH_VERDICT.SETUP]:    { color: 'text-blue-400',    bg: 'bg-blue-500/10',     border: 'border-blue-500/20',    icon: Settings },

  [RISK_VERDICT.STABLE]:  { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: Shield },
  [RISK_VERDICT.WATCH]:   { color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   icon: AlertTriangle },
  [RISK_VERDICT.AT_RISK]: { color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20',     icon: XCircle },

  [CONFIG_VERDICT.OK]:      { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle },
  [CONFIG_VERDICT.WARNING]: { color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   icon: Settings },
  [CONFIG_VERDICT.BROKEN]:  { color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20',     icon: XCircle },

  [VALUE_VERDICT.INCREASING]: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: TrendingUp },
  [VALUE_VERDICT.FLAT]:       { color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/20',   icon: Minus },
  [VALUE_VERDICT.WEAK]:       { color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20',     icon: TrendingDown },
  [VALUE_VERDICT.UNKNOWN]:    { color: 'text-slate-500',   bg: 'bg-slate-500/10',   border: 'border-slate-500/20',   icon: Clock },
};

function getVerdictStyle(verdict) {
  return VERDICT_STYLE[verdict] || { color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20', icon: Minus };
}

// ============================================================================
// PERIOD OPTIONS
// ============================================================================

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

// ============================================================================
// FORMATTING
// ============================================================================

function pct(v) {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v * 100)}%`;
}

function convLabel(v) {
  if (v === null) return 'No Not-Yet';
  if (v === undefined) return 'N/A';
  return `${Math.round(v * 100)}%`;
}

function num(v) {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString();
}

function hrs(minutes) {
  if (!minutes) return '0h 0min';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const VerdictPulseCard = ({ title, verdict, subtitle, detail }) => {
  const style = getVerdictStyle(verdict);
  const Icon = style.icon;
  return (
    <div className={`${style.bg} border ${style.border} rounded-2xl p-4 md:p-5`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${style.color}`} />
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</span>
      </div>
      <p className={`text-lg md:text-xl font-semibold ${style.color} mb-1`}>{verdict}</p>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      {detail && <p className="text-xs text-slate-600 mt-1">{detail}</p>}
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, subValue, color = 'text-slate-300' }) => (
  <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <p className={`text-xl font-semibold text-white`}>{value}</p>
    {subValue && <p className="text-xs text-slate-600 mt-1">{subValue}</p>}
  </div>
);

const RiskBadge = ({ code, severity }) => {
  const sev = severity || RISK_SEVERITY[code] || 'warning';
  const colorClass = sev === 'critical'
    ? 'bg-red-500/10 border-red-500/20 text-red-400'
    : sev === 'info' || sev === 'setup'
      ? 'bg-slate-500/10 border-slate-500/20 text-slate-400'
      : 'bg-amber-500/10 border-amber-500/20 text-amber-400';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${colorClass}`}>
      <AlertTriangle className="w-3 h-3" />
      {RISK_LABELS[code] || code}
    </span>
  );
};

const RiskCell = ({ risks }) => (
  <ul className="text-left space-y-0.5">
    {risks.map((r, i) => {
      const sev = r.severity || RISK_SEVERITY[r.code] || 'warning';
      const colorClass = sev === 'critical' ? 'text-red-400'
        : sev === 'info' || sev === 'setup' ? 'text-slate-400'
        : 'text-amber-400';
      return (
        <li key={i} className={`text-[10px] leading-tight ${colorClass} flex items-start gap-1`}>
          <span className="mt-[3px] shrink-0 w-1 h-1 rounded-full bg-current" />
          {RISK_LABELS[r.code]?.split('(')[0]?.trim() || r.code}
        </li>
      );
    })}
  </ul>
);

function getPrimaryAction(risks) {
  const codes = risks.map(r => r.code);
  if (codes.some(c => [RISK.NO_PACKS, RISK.NO_SPECIALTIES, RISK.NO_USERS].includes(c)))
    return 'Fix Configuration';
  if (codes.includes(RISK.MISSING_SLA) || codes.includes(RISK.DEFAULT_SETTINGS))
    return 'Open Impact / SLA Settings';
  if (codes.includes(RISK.NO_SUBMISSIONS))
    return 'Review Tenant Adoption';
  if (codes.includes(RISK.STALE_DATA))
    return 'View Rollup Status';
  if (codes.includes(RISK.OPS_ERRORS))
    return 'Open Ops Errors';
  return 'Open Ops Command Center';
}

const TenantRiskRow = ({ summary }) => {
  const healthStyle = getVerdictStyle(summary.verdicts.health);
  const HealthIcon = healthStyle.icon;
  const action = getPrimaryAction(summary.risks);

  return (
    <div className="bg-slate-800/30 border border-slate-700/40 rounded-xl p-3 md:p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <HealthIcon className={`w-4 h-4 shrink-0 ${healthStyle.color}`} />
          <span className="text-sm font-medium text-white truncate">{summary.tenantName}</span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${healthStyle.color} ${healthStyle.bg}`}>
          {summary.verdicts.health}
        </span>
      </div>
      <p className="text-xs text-slate-500">
        {summary.risks.length} risk{summary.risks.length !== 1 ? 's' : ''} · {num(summary.metrics.gateSubmissions)} submissions
      </p>
      <div className="flex flex-wrap gap-1.5">
        {summary.risks.map((r, i) => <RiskBadge key={i} code={r.code} severity={r.severity} />)}
      </div>
      <div className="pt-1">
        <span className="text-[11px] text-slate-500">Primary action: </span>
        <span className="text-[11px] text-indigo-400 font-medium">{action}</span>
      </div>
    </div>
  );
};

// ============================================================================
// TENANT HEALTH TABLE
// ============================================================================

const TenantHealthTable = ({ summaries }) => {
  const [sortBy, setSortBy] = useState('risks');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const sevRank = { critical: 0, warning: 1, setup: 2, info: 3 };
    const worstSev = (risks) => risks.length === 0 ? 4 : Math.min(...risks.map(r => sevRank[r.severity] ?? sevRank[RISK_SEVERITY[r.code]] ?? 1));
    const lifecycleRank = (s) => s.tenantStatus === 'disabled' ? 2 : s.preLaunch ? 1 : 0;

    const copy = [...summaries];
    copy.sort((a, b) => {
      const lcDiff = lifecycleRank(a) - lifecycleRank(b);
      if (lcDiff !== 0) return lcDiff;

      let av, bv;
      switch (sortBy) {
        case 'name':       av = a.tenantName; bv = b.tenantName; break;
        case 'health':     av = a.verdicts.health; bv = b.verdicts.health; break;
        case 'risks': {
          const sevDiff = worstSev(a.risks) - worstSev(b.risks);
          if (sevDiff !== 0) return sortDir === 'asc' ? -sevDiff : sevDiff;
          av = a.risks.length; bv = b.risks.length; break;
        }
        case 'submissions': av = a.metrics.gateSubmissions; bv = b.metrics.gateSubmissions; break;
        case 'minutes':    av = a.metrics.minutesReclaimed; bv = b.metrics.minutesReclaimed; break;
        default: {
          const sevDiff = worstSev(a.risks) - worstSev(b.risks);
          if (sevDiff !== 0) return sortDir === 'asc' ? -sevDiff : sevDiff;
          av = a.risks.length; bv = b.risks.length; break;
        }
      }
      if (typeof av === 'string') {
        const cmp = av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [summaries, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ col, children, className = '' }) => (
    <button
      onClick={() => toggleSort(col)}
      className={`flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-300 transition-colors uppercase tracking-wider ${className}`}
    >
      {children}
      {sortBy === col && (
        sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
      )}
    </button>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm table-fixed">
        <colgroup>
          <col className="w-[20%]" />
          <col className="w-[12%]" />
          <col className="w-[22%]" />
          <col className="w-[12%]" />
          <col className="w-[12%]" />
          <col className="w-[11%]" />
          <col className="w-[11%]" />
        </colgroup>
        <thead>
          <tr className="border-b border-slate-700/50">
            <th className="text-left py-3 px-3"><SortHeader col="name">Tenant</SortHeader></th>
            <th className="text-left py-3 px-3"><SortHeader col="health">Health</SortHeader></th>
            <th className="text-center py-3 px-3"><SortHeader col="risks" className="justify-center">Risks</SortHeader></th>
            <th className="text-center py-3 px-3"><SortHeader col="submissions" className="justify-center">Submissions</SortHeader></th>
            <th className="text-center py-3 px-3"><SortHeader col="minutes" className="justify-center">Time Saved</SortHeader></th>
            <th className="text-center py-3 px-3"><span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Config</span></th>
            <th className="text-center py-3 px-3"><span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Value</span></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(s => {
            const hs = getVerdictStyle(s.verdicts.health);
            const cs = getVerdictStyle(s.verdicts.config);
            const vs = getVerdictStyle(s.verdicts.value);
            const HIcon = hs.icon;
            const notLive = s.preLaunch || s.tenantStatus === 'disabled';
            const lifecycleLabel = <span className="text-slate-500 text-xs italic">{s.tenantStatus === 'disabled' ? 'Disabled' : 'Pre-launch'}</span>;
            return (
              <tr key={s.tenantId} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                <td className="py-3 px-3">
                  <span className="text-white font-medium">{s.tenantName}</span>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center gap-1.5 ${hs.color}`}>
                    <HIcon className="w-3.5 h-3.5" />
                    <span className="text-xs">{s.verdicts.health}</span>
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  {notLive ? lifecycleLabel : s.risks.length > 0 ? (
                    <RiskCell risks={s.risks} />
                  ) : (
                    <span className="text-slate-600 text-xs">No active risks</span>
                  )}
                </td>
                <td className="py-3 px-3 text-center text-slate-300">{num(s.metrics.gateSubmissions)}</td>
                <td className="py-3 px-3 text-center text-slate-300">{hrs(s.metrics.minutesReclaimed)}</td>
                <td className="py-3 px-3 text-center">
                  {notLive ? lifecycleLabel : <span className={`text-xs ${cs.color}`}>{s.verdicts.config}</span>}
                </td>
                <td className="py-3 px-3 text-center">
                  {notLive ? lifecycleLabel : (
                    <>
                      <span className={`text-xs ${vs.color}`}>{s.verdicts.value}</span>
                      {s.valueReason && <p className="text-[10px] text-slate-500 mt-0.5">{s.valueReason}</p>}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PlatformDashboard = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadPlatformHealth(period);
      setData(result);
    } catch (err) {
      console.error('Platform health load failed:', err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [period]);

  const summaries = data?.summaries || [];
  const isOperational = (s) => !s.preLaunch && s.tenantStatus !== 'disabled';
  const liveSummaries = useMemo(() => summaries.filter(isOperational), [summaries]);

  const liveAggregate = useMemo(() => {
    const counts = {
      health: { [HEALTH_VERDICT.HEALTHY]: 0, [HEALTH_VERDICT.WARNING]: 0, [HEALTH_VERDICT.CRITICAL]: 0, [HEALTH_VERDICT.SETUP]: 0 },
      risk:   { [RISK_VERDICT.STABLE]: 0, [RISK_VERDICT.WATCH]: 0, [RISK_VERDICT.AT_RISK]: 0 },
      config: { [CONFIG_VERDICT.OK]: 0, [CONFIG_VERDICT.WARNING]: 0, [CONFIG_VERDICT.BROKEN]: 0 },
      value:  { [VALUE_VERDICT.INCREASING]: 0, [VALUE_VERDICT.FLAT]: 0, [VALUE_VERDICT.WEAK]: 0, [VALUE_VERDICT.UNKNOWN]: 0 },
    };
    for (const s of liveSummaries) {
      counts.health[s.verdicts.health]++;
      counts.risk[s.verdicts.risk]++;
      counts.config[s.verdicts.config]++;
      counts.value[s.verdicts.value]++;
    }
    return {
      counts,
      totalMinutes: liveSummaries.reduce((a, s) => a + s.metrics.minutesReclaimed, 0),
      totalSubmissions: liveSummaries.reduce((a, s) => a + s.metrics.gateSubmissions, 0),
      totalErrors: liveSummaries.reduce((a, s) => a + s.metrics.unresolvedErrorCount, 0),
      tenantCount: liveSummaries.length,
    };
  }, [liveSummaries]);

  const tenantsWithRisks = useMemo(() => liveSummaries.filter(s => s.risks.length > 0).sort((a, b) => {
    const sevRank = { critical: 0, warning: 1, setup: 2, info: 3 };
    const worstSev = (risks) => Math.min(...risks.map(r => sevRank[r.severity] ?? sevRank[RISK_SEVERITY[r.code]] ?? 1));
    const diff = worstSev(a.risks) - worstSev(b.risks);
    if (diff !== 0) return diff;
    return b.risks.length - a.risks.length;
  }), [liveSummaries]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
          <p className="text-sm text-slate-500">Loading platform health across all tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-center">
          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 font-medium mb-2">Failed to load platform health</p>
          <p className="text-sm text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || summaries.length === 0) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8 text-center">
          <Building className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 font-medium mb-1">No tenants found</p>
          <p className="text-sm text-slate-500">Create a tenant to see platform health data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium text-amber-400 uppercase">Platform Admin</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-1">Platform Command Center</h2>
          <p className="text-slate-400 text-sm">{liveAggregate.tenantCount} live tenant{liveAggregate.tenantCount !== 1 ? 's' : ''} monitored{summaries.length > liveAggregate.tenantCount ? ` (${summaries.length} total)` : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <div className="flex items-center bg-slate-800/60 rounded-lg border border-slate-700/50 p-0.5">
            {PERIODS.map(p => (
              <button
                key={p.days}
                onClick={() => setPeriod(p.days)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.days
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={loadData}
            className="p-2 rounded-lg border border-slate-700/50 hover:bg-slate-700/40 text-slate-400 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Section 1: Platform Pulse Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <VerdictPulseCard
          title="Tenant Health"
          verdict={
            liveAggregate.counts.health[HEALTH_VERDICT.CRITICAL] > 0
              ? HEALTH_VERDICT.CRITICAL
              : liveAggregate.counts.health[HEALTH_VERDICT.WARNING] > 0
                ? HEALTH_VERDICT.WARNING
                : HEALTH_VERDICT.HEALTHY
          }
          subtitle={`${liveAggregate.counts.health[HEALTH_VERDICT.HEALTHY]} healthy, ${liveAggregate.counts.health[HEALTH_VERDICT.WARNING]} warning, ${liveAggregate.counts.health[HEALTH_VERDICT.CRITICAL]} critical`}
          detail={liveAggregate.counts.health[HEALTH_VERDICT.SETUP] > 0 ? `${liveAggregate.counts.health[HEALTH_VERDICT.SETUP]} in setup` : null}
        />
        <VerdictPulseCard
          title="Tenant Risk"
          verdict={
            liveAggregate.counts.risk[RISK_VERDICT.AT_RISK] > 0
              ? RISK_VERDICT.AT_RISK
              : liveAggregate.counts.risk[RISK_VERDICT.WATCH] > 0
                ? RISK_VERDICT.WATCH
                : RISK_VERDICT.STABLE
          }
          subtitle={`${liveAggregate.counts.risk[RISK_VERDICT.STABLE]} stable, ${liveAggregate.counts.risk[RISK_VERDICT.WATCH]} watch, ${liveAggregate.counts.risk[RISK_VERDICT.AT_RISK]} at risk`}
        />
        <VerdictPulseCard
          title="Configuration"
          verdict={
            liveAggregate.counts.config[CONFIG_VERDICT.BROKEN] > 0
              ? CONFIG_VERDICT.BROKEN
              : liveAggregate.counts.config[CONFIG_VERDICT.WARNING] > 0
                ? CONFIG_VERDICT.WARNING
                : CONFIG_VERDICT.OK
          }
          subtitle={`${liveAggregate.counts.config[CONFIG_VERDICT.OK]} ok, ${liveAggregate.counts.config[CONFIG_VERDICT.WARNING]} warning, ${liveAggregate.counts.config[CONFIG_VERDICT.BROKEN]} broken`}
        />
        {(() => {
          const valueVerdict = liveAggregate.counts.value[VALUE_VERDICT.WEAK] > 0
            ? VALUE_VERDICT.WEAK
            : liveAggregate.counts.value[VALUE_VERDICT.INCREASING] > 0
              ? VALUE_VERDICT.INCREASING
              : liveAggregate.counts.value[VALUE_VERDICT.FLAT] > 0
                ? VALUE_VERDICT.FLAT
                : VALUE_VERDICT.UNKNOWN;
          const unknownReasons = valueVerdict === VALUE_VERDICT.UNKNOWN
            ? liveSummaries.filter(s => s.verdicts.value === VALUE_VERDICT.UNKNOWN && s.valueReason).map(s => s.valueReason)
            : [];
          const topReason = unknownReasons.length > 0 ? unknownReasons[0] : null;
          return (
            <VerdictPulseCard
              title="Platform Value"
              verdict={valueVerdict}
              subtitle={`${hrs(liveAggregate.totalMinutes)} reclaimed across ${num(liveAggregate.totalSubmissions)} submissions`}
              detail={topReason}
            />
          );
        })()}
      </div>

      {/* Section 2: Risk Row */}
      {tenantsWithRisks.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Tenants with Active Risks ({tenantsWithRisks.length})
          </h3>
          <div className="space-y-2">
            {tenantsWithRisks.map(s => <TenantRiskRow key={s.tenantId} summary={s} />)}
          </div>
        </div>
      )}

      {/* Section 3: Platform Metrics */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-400" />
          Platform Adoption &amp; Value
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={Activity}
            label="Total Submissions"
            value={num(liveAggregate.totalSubmissions)}
            subValue={`Last ${period}d`}
            color="text-blue-400"
          />
          <MetricCard
            icon={Zap}
            label="Time Reclaimed"
            value={hrs(liveAggregate.totalMinutes)}
            subValue={`${num(liveAggregate.totalSubmissions)} referrals processed`}
            color="text-emerald-400"
          />
          <MetricCard
            icon={Building}
            label="Active Tenants"
            value={num(liveSummaries.filter(s => s.metrics.gateSubmissions > 0).length)}
            subValue={`of ${liveAggregate.tenantCount} live`}
            color="text-violet-400"
          />
          <MetricCard
            icon={AlertTriangle}
            label="Unresolved Errors"
            value={num(liveAggregate.totalErrors)}
            subValue={liveAggregate.totalErrors > 0 ? 'Across all tenants' : 'All clear'}
            color={liveAggregate.totalErrors > 0 ? 'text-red-400' : 'text-emerald-400'}
          />
        </div>
      </div>

      {/* Section 4: Tenant Health Table */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Building className="w-4 h-4 text-slate-400" />
          All Tenants
        </h3>
        <div className="bg-slate-800/30 border border-slate-700/40 rounded-xl overflow-hidden">
          <TenantHealthTable summaries={summaries} />
        </div>
      </div>
    </div>
  );
};

export default PlatformDashboard;
